import { delay } from 'es-toolkit/promise';
import { Building2 } from 'lucide-react';
import { type ReactNode, useEffect } from 'react';
import { useNavigate } from 'react-router';
import { useAsyncData } from '../../hooks/useAsyncData';
import { useAuth } from '../../hooks/useAuth';
import { usePublisher } from '../../hooks/usePublisher';
import { useTRPC } from '../../hooks/useTRPC';
import type { KybStatusData } from '../../lib/verificationStatus';
import { captureAndReportError } from '../../utils/errorReporting';
import { getPublisherVerificationGate } from '../../utils/publisherVerification';
import { formatShortDate } from '../../utils/stringHelpers';
import { Button } from '../Button';
import { InitialsAvatar, StatusPill, type StatusPillTone } from '../portal';
import {
  SectionSkeleton,
  SettingsFact,
  SettingsNotice,
  SettingsPage,
  SettingsRailCard,
  SettingsSection,
} from '../settings';

type VerificationStatus = KybStatusData['status'];

const PAGE_DESCRIPTION = 'Your publisher identity and verification status.';

const VERIFICATION_DESCRIPTION =
  'Seeker needs to verify your information to comply with regulations.';

const VERIFICATION_TREATMENTS: Record<
  VerificationStatus,
  { tone: StatusPillTone; label: string; cta?: string }
> = {
  NotStarted: {
    tone: 'required',
    label: 'Not started',
    cta: 'Start verification',
  },
  InProgress: {
    tone: 'waiting',
    label: 'In progress',
    cta: 'Continue verification',
  },
  UnderReview: { tone: 'review', label: 'Under review' },
  RequiresAdditionalInfo: {
    tone: 'required',
    label: 'Info needed',
    cta: 'Provide more info',
  },
  Rejected: {
    tone: 'rejected',
    label: 'Rejected',
    cta: 'Resubmit verification',
  },
  Approved: { tone: 'published', label: 'Verified' },
};

function getVerificationMessage(
  status: VerificationStatus,
  kybStatus: KybStatusData | null,
): string {
  switch (status) {
    case 'InProgress':
      return 'Pick up where you left off.';
    case 'UnderReview': {
      const submitted = formatShortDate(kybStatus?.submittedAt);
      return submitted
        ? `Submitted ${submitted}. We'll email you when it's done.`
        : "We'll email you when it's done.";
    }
    case 'RequiresAdditionalInfo':
      return (
        kybStatus?.additionalInfoRequested ||
        'We need more information before we can finish your review.'
      );
    case 'Rejected':
      return (
        kybStatus?.rejectionReason ||
        'Your verification was rejected. Resubmit to try again.'
      );
    case 'Approved': {
      // Only trust the completion date when the API really says Approved: the
      // bypass flag can show this treatment over any underlying status.
      const completed =
        kybStatus?.status === 'Approved'
          ? formatShortDate(kybStatus.reviewCompletedAt)
          : '';
      return completed
        ? `Your publisher account is verified. Approved ${completed}.`
        : 'Your publisher account is verified.';
    }
    default:
      return 'Verify your identity to add dApps and publish releases.';
  }
}

/** Same `SettingsPage` element type for every state, so nothing remounts. */
function PageFrame({
  rail,
  children,
}: {
  rail?: ReactNode;
  children: ReactNode;
}) {
  return (
    <SettingsPage
      icon={<Building2 />}
      title="Publisher"
      description={PAGE_DESCRIPTION}
      rail={rail}
    >
      {children}
    </SettingsPage>
  );
}

export const PublisherProfilePage = () => {
  const [authState] = useAuth();
  const { activePublisher } = usePublisher();
  const trpc = useTRPC();
  const navigate = useNavigate();
  const publisherUserJoinId = activePublisher?.publisherUserJoinId;

  const {
    data: kybStatus,
    isLoading,
    setIsLoading,
    run,
  } = useAsyncData<KybStatusData | null>({
    initialData: null,
    initialLoading: true,
  });

  useEffect(() => {
    const loadKybStatus = async () => {
      if (authState.type !== 'Authenticated') {
        if (authState.type === 'NoToken') {
          setIsLoading(false);
        }
        return;
      }

      await delay(100);

      await run(() => trpc.getKybStatus.query(undefined), {
        showLoadingState: false,
        onSuccess: (kybResult, helpers) => {
          if ('_tag' in kybResult && kybResult._tag === 'Right') {
            helpers.setData(kybResult.right);
          }
          helpers.setIsLoading(false);
        },
        onError: (error, helpers) => {
          captureAndReportError('Failed to load KYB status:', error);
          helpers.setIsLoading(false);
        },
      });
    };

    loadKybStatus();
    // `publisherUserJoinId` re-runs the query when the active publisher changes.
  }, [authState, publisherUserJoinId, run, setIsLoading, trpc]);

  if (isLoading || authState.type === 'Verifying') {
    return (
      <PageFrame>
        <SectionSkeleton rows={2} variant="fact" />
        <SectionSkeleton rows={5} variant="fact" />
      </PageFrame>
    );
  }

  if (!activePublisher) {
    return (
      <PageFrame>
        <SettingsNotice
          tone="info"
          title="No publisher profile found"
          description="Create or join a publisher account to see its identity and verification status here."
        />
      </PageFrame>
    );
  }

  const {
    acceptedAt,
    avatarThumbnailUrl,
    avatarUrl,
    isFrozen,
    isOwner,
    kycKybBypassEnabled,
    name,
    publisherType,
    role,
    website,
  } = activePublisher;
  const isIndividual = publisherType === 'Individual';
  const { isApproved } = getPublisherVerificationGate({
    status: kybStatus?.status ?? activePublisher.kybStatus,
    publisherType,
    kycKybBypassEnabled,
  });
  const status: VerificationStatus = isApproved
    ? 'Approved'
    : (kybStatus?.status ?? activePublisher.kybStatus ?? 'NotStarted');
  const treatment = VERIFICATION_TREATMENTS[status];
  const verificationRoute = isIndividual
    ? '/dashboard/submit-kyc'
    : '/dashboard/submit-kyb';

  return (
    <PageFrame
      rail={
        <>
          {isApproved ? null : (
            <SettingsRailCard title="What verification unlocks">
              <ul>
                <li>Add dApps to this publisher account.</li>
                <li>Upload releases.</li>
                <li>Publish to the dApp Store.</li>
              </ul>
            </SettingsRailCard>
          )}
          <SettingsRailCard title="Changing these details">
            <p>
              Your publisher name, website and avatar are set during
              verification. Contact support to change them.
            </p>
          </SettingsRailCard>
        </>
      }
    >
      <div className="motion-enter flex flex-col gap-6">
        <SettingsSection
          title="Verification"
          description={VERIFICATION_DESCRIPTION}
          action={
            <StatusPill tone={treatment.tone}>{treatment.label}</StatusPill>
          }
        >
          <p className="text-sm leading-5 text-seeker-400">
            {getVerificationMessage(status, kybStatus)}
          </p>
          {treatment.cta ? (
            <Button
              variant="primary"
              className="w-full sm:w-auto sm:self-start"
              onClick={() => navigate(verificationRoute)}
            >
              {treatment.cta}
            </Button>
          ) : null}
        </SettingsSection>

        <SettingsSection
          title="Identity"
          action={
            isFrozen ? <StatusPill tone="suspended">Frozen</StatusPill> : null
          }
        >
          {isIndividual ? (
            <SettingsFact
              label="Avatar"
              value={
                <InitialsAvatar
                  name={name}
                  imageUrl={avatarThumbnailUrl || avatarUrl}
                  size={96}
                />
              }
            />
          ) : null}
          <SettingsFact label="Name" value={name} />
          <SettingsFact label="Website" value={website} />
          <SettingsFact
            label="Publisher type"
            value={`${publisherType} · ${isIndividual ? 'KYC' : 'KYB'} required`}
          />
          <SettingsFact
            label="Your role"
            value={<StatusPill tone="neutral">{role}</StatusPill>}
            hint={isOwner ? 'You own this publisher account.' : undefined}
          />
          <SettingsFact
            label="Member since"
            value={formatShortDate(acceptedAt)}
            valueClassName="tabular-nums"
          />
        </SettingsSection>
      </div>
    </PageFrame>
  );
};
